import { Col, Form, Input, Row, Select } from 'antd';
import React from 'react';

import { Card, ErrorHandler, Gap, PhoneInput, Text } from '@common/components';
import { useQuery } from '@common/hooks';
import { TimeZonesQueryResponse, US_STATES_QUERY } from '@institutions/graphql';
import { SupplierSignUpPageLogOffOptionsSelect } from './_supplier-sign-up-page-log-off-options-select';

export const SupplierSignupPageOrganizationDetailsForm = ({
  formSubmitting,
  timeZonesQueryData,
}: {
  formSubmitting: boolean;
  timeZonesQueryData: TimeZonesQueryResponse | undefined;
}) => {
  const { data: usStatesQueryData, error } = useQuery(US_STATES_QUERY);

  const defaultRule = { required: true, message: "Value can't be blank" };

  if (error) {
    return <ErrorHandler error={error} log="SupplierSignupForm" />;
  }

  return (
    <>
      <Form.Item
        style={{ width: '100%' }}
        label="Name"
        name="supplierName"
        rules={[
          {
            required: true,
            message: 'Please enter the supplier name',
          },
        ]}
      >
        <Input
          size="large"
          placeholder="e.g., Awesome Staffing Inc."
          disabled={formSubmitting}
        />
      </Form.Item>

      <Text size="lg">Contact and Location</Text>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            name="supplierPhone"
            label="Phone"
            rules={[
              { required: true, message: 'Please enter a phone number' },
              {
                pattern: /^(\d{3}-){2}\d{4}$/,
                message: 'Must be in xxx-xxx-xxxx format',
              },
            ]}
          >
            <PhoneInput size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="Website"
            name="supplierWebsite"
            rules={[
              {
                required: true,
                message: 'Please enter the website',
              },
            ]}
          >
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={18}>
          <Form.Item
            name="supplierAddress"
            label="Address"
            rules={[defaultRule]}
          >
            <Input size="large" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="supplierPostalCode"
            label="Postal Code"
            rules={[defaultRule]}
          >
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={4}>
          <Form.Item
            name="supplierStateCode"
            label="State"
            rules={[defaultRule]}
          >
            <Select size="large" showSearch>
              {usStatesQueryData?.usStates.map(
                (state: { text: string; value: string }) => (
                  <Select.Option key={state.value} value={state.value}>
                    {state.text}
                  </Select.Option>
                )
              )}
            </Select>
          </Form.Item>
        </Col>

        <Col span={14}>
          <Form.Item name="supplierCity" label="City" rules={[defaultRule]}>
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="supplierCountry"
            label="Country"
            rules={[defaultRule]}
          >
            <Input size="large" disabled={formSubmitting} />
          </Form.Item>
        </Col>
      </Row>

      <Card>
        <Gap size="md" vertical>
          <Text size="lg">System Settings</Text>

          <Form.Item
            style={{ width: '100%' }}
            name="supplierTimeZone"
            label="Timezone"
            rules={[defaultRule]}
          >
            <Select size="large" disabled={formSubmitting}>
              {timeZonesQueryData?.timeZones.map(
                (timeZone: { text: string; value: string }) => (
                  <Select.Option key={timeZone.value} value={timeZone.value}>
                    {timeZone.text}
                  </Select.Option>
                )
              )}
            </Select>
          </Form.Item>

          <Form.Item
            style={{ width: '100%' }}
            name="logOffUsersInSeconds"
            label="Log off users period"
          >
            <SupplierSignUpPageLogOffOptionsSelect disabled={formSubmitting} />
          </Form.Item>
        </Gap>
      </Card>
    </>
  );
};
